/**
 * HrPositionsPage 컴포넌트
 *
 * 직위(Position Level) 마스터 관리 페이지입니다.
 *
 * **기능:**
 * - 직위 목록 조회 및 표시
 * - 직위 생성/수정/삭제 (CRUD)
 * - 검색 및 필터링
 * - 정렬 순서 관리
 *
 * **권한:**
 * - hr.manage 권한 필요 (HR, Super Admin만 접근 가능)
 */

import React, { useState } from 'react';
import { Search, Plus, Edit2, Trash2, Loader, AlertCircle } from 'lucide-react';
import { usePositionLevels } from '../../shared/hooks/usePositionLevels';
import { usePositionLevelMutations } from '../../shared/hooks/usePositionLevelMutations';
import { PositionLevelFormModal } from './components/PositionLevelFormModal';

/**
 * 직위 관리 페이지 컴포넌트
 */
export function HrPositionsPage() {
  // 검색/필터 상태
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // 모달 상태
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPositionLevelId, setEditingPositionLevelId] = useState<string | undefined>(
    undefined
  );

  // 데이터 조회
  const { positionLevels, isLoading, error, refetch } = usePositionLevels({
    searchText,
    status: statusFilter,
  });

  // Mutations
  const { deletePositionLevel } = usePositionLevelMutations();

  /**
   * 직위 추가 버튼 클릭
   */
  const handleAdd = () => {
    setEditingPositionLevelId(undefined); // 생성 모드
    setIsModalOpen(true);
  };

  /**
   * 직위 수정 버튼 클릭
   */
  const handleEdit = (positionLevelId: string) => {
    setEditingPositionLevelId(positionLevelId); // 수정 모드
    setIsModalOpen(true);
  };

  /**
   * 직위 삭제 버튼 클릭
   */
  const handleDelete = async (positionLevelId: string, positionLevelName: string) => {
    const confirmed = window.confirm(
      `"${positionLevelName}" 직위를 삭제하시겠습니까?\n\n` +
        `소프트 삭제되며, 사용 여부가 "미사용"으로 변경됩니다.\n` +
        `해당 직위를 사용 중인 직원이 있어도 삭제 가능합니다.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await deletePositionLevel(positionLevelId);
      alert('직위가 삭제되었습니다.');
      refetch(); // 목록 새로고침
    } catch (err: any) {
      console.error('직위 삭제 실패:', err);
      alert(err.message || '직위 삭제에 실패했습니다.');
    }
  };

  /**
   * 모달 저장 성공 시
   */
  const handleModalSuccess = () => {
    refetch(); // 목록 새로고침
    setIsModalOpen(false);
  };

  /**
   * 필터 초기화
   */
  const handleResetFilters = () => {
    setSearchText('');
    setStatusFilter('all');
  };

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>직위 관리</h1>
          <p style={styles.subtitle}>직위(호칭) 마스터 데이터를 관리합니다.</p>
        </div>
        <button
          style={styles.addButton}
          onClick={handleAdd}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 150, 136, 0.3)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
        >
          <Plus size={20} />
          <span>직위 추가</span>
        </button>
      </div>

      {/* 검색/필터 영역 */}
      <div style={styles.filterSection}>
        {/* 검색 */}
        <div style={styles.searchBox}>
          <Search size={20} style={{ color: 'var(--color-gray-400)' }} />
          <input
            type="text"
            placeholder="직위명, 코드 검색..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {/* 사용 여부 필터 */}
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={styles.select}
        >
          <option value="all">전체</option>
          <option value="active">사용</option>
          <option value="inactive">미사용</option>
        </select>

        {/* 필터 초기화 */}
        {(searchText || statusFilter !== 'all') && (
          <button style={styles.resetButton} onClick={handleResetFilters}>
            필터 초기화
          </button>
        )}
      </div>

      {/* 로딩 상태 */}
      {isLoading && (
        <div style={styles.loadingBox}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <p style={{ marginTop: '12px', color: 'var(--color-gray-600)' }}>
            직위 목록을 불러오는 중...
          </p>
        </div>
      )}

      {/* 에러 상태 */}
      {error && !isLoading && (
        <div style={styles.errorBox}>
          <AlertCircle size={32} color="var(--color-error)" />
          <p style={{ marginTop: '12px', color: 'var(--color-error)', fontWeight: '500' }}>
            직위 목록을 불러오는데 실패했습니다.
          </p>
          <p style={{ marginTop: '4px', color: 'var(--color-gray-600)', fontSize: '14px' }}>
            {error.message}
          </p>
          <button style={styles.retryButton} onClick={() => refetch()}>
            다시 시도
          </button>
        </div>
      )}

      {/* 직위 목록 테이블 */}
      {!isLoading && !error && (
        <>
          {positionLevels.length === 0 ? (
            <div style={styles.emptyBox}>
              <p style={{ color: 'var(--color-gray-600)' }}>
                {searchText || statusFilter !== 'all'
                  ? '검색 결과가 없습니다.'
                  : '등록된 직위가 없습니다. "직위 추가" 버튼을 클릭하여 새 직위를 추가하세요.'}
              </p>
            </div>
          ) : (
            <div style={styles.tableContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>정렬 순서</th>
                    <th style={styles.th}>직위명</th>
                    <th style={styles.th}>코드</th>
                    <th style={styles.th}>위계 가중치</th>
                    <th style={styles.th}>승진 기준 (년)</th>
                    <th style={styles.th}>사용 여부</th>
                    <th style={styles.th}>설명</th>
                    <th style={styles.th}>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {positionLevels.map(positionLevel => (
                    <tr
                      key={positionLevel.id}
                      style={styles.tr}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <td style={styles.td}>{positionLevel.order}</td>
                      <td style={styles.td}>
                        <strong>{positionLevel.name}</strong>
                      </td>
                      <td style={styles.td}>{positionLevel.code || '-'}</td>
                      <td style={styles.td}>{positionLevel.rankWeight}</td>
                      <td style={styles.td}>
                        {positionLevel.requiredRecognizedYearsFromHire !== null
                          ? `${positionLevel.requiredRecognizedYearsFromHire}년 이상`
                          : '-'}
                      </td>
                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            backgroundColor:
                              positionLevel.status === 'active'
                                ? 'var(--color-primary-lighter)'
                                : 'var(--color-gray-200)',
                            color:
                              positionLevel.status === 'active'
                                ? 'var(--color-primary)'
                                : 'var(--color-gray-600)',
                          }}
                        >
                          {positionLevel.status === 'active' ? '사용' : '미사용'}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {positionLevel.description ? (
                          <span
                            style={{
                              display: 'block',
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={positionLevel.description}
                          >
                            {positionLevel.description}
                          </span>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actionButtons}>
                          {/* 수정 버튼 */}
                          <button
                            style={styles.editButton}
                            onClick={() => handleEdit(positionLevel.id)}
                            title="수정"
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = '#2196F3';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = '#42A5F5';
                            }}
                          >
                            <Edit2 size={16} />
                          </button>

                          {/* 삭제 버튼 */}
                          <button
                            style={styles.deleteButton}
                            onClick={() => handleDelete(positionLevel.id, positionLevel.name)}
                            title="삭제"
                            onMouseEnter={e => {
                              e.currentTarget.style.backgroundColor = '#D32F2F';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.backgroundColor = '#F44336';
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 결과 개수 */}
          {positionLevels.length > 0 && (
            <div style={styles.footer}>
              <p style={{ color: 'var(--color-gray-600)', fontSize: '14px' }}>
                총 <strong>{positionLevels.length}</strong>개의 직위
              </p>
            </div>
          )}
        </>
      )}

      {/* 직위 추가/수정 모달 */}
      <PositionLevelFormModal
        isOpen={isModalOpen}
        positionLevelId={editingPositionLevelId}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}

/**
 * 스타일
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: 'var(--color-gray-800)',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: 'var(--color-gray-600)',
    margin: 0,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  filterSection: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap' as const,
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: '300px',
    padding: '10px 16px',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
  },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    color: 'var(--color-gray-800)',
  },
  select: {
    padding: '10px 16px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    backgroundColor: 'white',
    cursor: 'pointer',
    outline: 'none',
  },
  resetButton: {
    padding: '10px 20px',
    fontSize: '14px',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  errorBox: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  retryButton: {
    marginTop: '16px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  emptyBox: {
    textAlign: 'center' as const,
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    border: '1px solid var(--color-gray-200)',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    padding: '16px',
    textAlign: 'left' as const,
    fontSize: '13px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    backgroundColor: 'var(--color-gray-50)',
    borderBottom: '2px solid var(--color-gray-200)',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    transition: 'background-color 0.2s',
  },
  td: {
    padding: '16px',
    fontSize: '14px',
    color: 'var(--color-gray-800)',
    borderBottom: '1px solid var(--color-gray-200)',
    whiteSpace: 'nowrap' as const,
  },
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: '500',
    borderRadius: '12px',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
  editButton: {
    padding: '6px 10px',
    backgroundColor: '#42A5F5',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s',
  },
  deleteButton: {
    padding: '6px 10px',
    backgroundColor: '#F44336',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s',
  },
  footer: {
    marginTop: '16px',
    textAlign: 'center' as const,
  },
};
